import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { io, Socket } from 'socket.io-client';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';
import { GlobalDropdownField, DropdownOption } from '../../../components/GlobalComponents/GlobalDropdownField';
import type { PracticalMaterial } from '../PracticalMaterials';
import type { LibraryMaterial } from './PracticalMaterialLibrarySidebar';

interface AddPracticalMaterialModalProps {
  visible: boolean;
  courseId: string;
  chapterId: string | null; // null → course-general material
  editingMaterial?: PracticalMaterial | null;
  reuseSource?: LibraryMaterial | null;
  onHide: () => void;
  onSuccess?: () => void;
}

interface UploadedFile { file: File; name: string; size: number; }
interface UploadedVideo { file: File; name: string; size: number; }
interface ReusedFile { key: string; file_name: string; size: number; }
interface ReusedVideo { video_id: string; original_name: string; }

interface VideoStatusEvent {
  video_id: string;
  status: 'ready' | 'failed';
  m3u8_url: string | null;
  error_message: string | null;
  updated_at: string;
}

let globalSocket: Socket | null = null;

const getOrCreateSocket = async (): Promise<Socket | null> => {
  if (globalSocket?.connected) return globalSocket;
  try {
    const res = await apiClient.get('/v1/socket-token');
    const { socket_token } = res.data;
    if (!socket_token) throw new Error('No socket_token in response');
    globalSocket = io(window.location.origin, {
      path: '/node-ws/socket.io',
      auth: { socket_token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });
    return globalSocket;
  } catch (err) {
    console.error('Socket init failed:', err);
    return null;
  }
};

const AddPracticalMaterialModal: React.FC<AddPracticalMaterialModalProps> = ({
  visible,
  courseId,
  chapterId,
  editingMaterial,
  reuseSource,
  onHide,
  onSuccess,
}) => {
  const toast         = useRef<Toast>(null);
  const fileInputRef  = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const pendingVideoIds = useRef<Set<string>>(new Set());
  const socketRef       = useRef<Socket | null>(null);

  const [loading,        setLoading]        = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error,          setError]          = useState<string | null>(null);
  const [uploadedFiles,  setUploadedFiles]  = useState<UploadedFile[]>([]);
  const [uploadedVideos, setUploadedVideos] = useState<UploadedVideo[]>([]);
  const [reusedFiles,    setReusedFiles]    = useState<ReusedFile[]>([]);
  const [reusedVideos,   setReusedVideos]   = useState<ReusedVideo[]>([]);
  const [videoProcessingStatus, setVideoProcessingStatus] = useState<Record<string, 'processing' | 'ready' | 'failed'>>({});

  const [formData, setFormData] = useState({
    title: '',
    body: '',
    status: 'active',
    order: 1,
    video_links: [''],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const MAX_FILES         = 3;
  const MAX_TOTAL_SIZE    = 20 * 1024 * 1024;
  const ALLOWED_FORMATS   = [
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg', 'image/jpg', 'image/png',
  ];
  const ALLOWED_EXTENSIONS = ['pdf', 'xls', 'xlsx', 'doc', 'docx', 'jpeg', 'jpg', 'png'];
  const MAX_VIDEOS          = 3;
  const MAX_VIDEO_SIZE      = 500 * 1024 * 1024;
  const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska'];
  const ALLOWED_VIDEO_EXTS  = ['mp4', 'mov', 'avi', 'mkv'];

  const statusOptions: DropdownOption[] = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Draft', value: 'draft' },
  ];

  const showSuccess = (summary: string, detail: string) => toast.current?.show({ severity: 'success', summary, detail, life: 3200 });
  const showError   = (detail: string, summary = 'Error') => toast.current?.show({ severity: 'error', summary, detail, life: 4500 });

  const handleVideoStatus = useCallback((data: VideoStatusEvent) => {
    if (!pendingVideoIds.current.has(data.video_id)) return;
    setVideoProcessingStatus((prev) => ({ ...prev, [data.video_id]: data.status }));
    if (data.status === 'ready') {
      toast.current?.show({ severity: 'success', summary: '✅ Video Ready', detail: 'A video has finished processing and is now available.', life: 5000 });
      pendingVideoIds.current.delete(data.video_id);
      onSuccess?.();
    }
    if (data.status === 'failed') {
      toast.current?.show({ severity: 'error', summary: '❌ Video Processing Failed', detail: data.error_message || 'A video failed to process. Please try uploading again.', life: 6000 });
      pendingVideoIds.current.delete(data.video_id);
    }
    if (pendingVideoIds.current.size === 0) {
      socketRef.current?.off('video_status', handleVideoStatus);
    }
  }, [onSuccess]);

  const initSocketListener = useCallback(async () => {
    const socket = await getOrCreateSocket();
    if (!socket) { console.warn('Could not connect to WebSocket'); return; }
    socketRef.current = socket;
    socket.off('video_status', handleVideoStatus);
    socket.on('video_status', handleVideoStatus);
  }, [handleVideoStatus]);

  useEffect(() => {
    return () => { socketRef.current?.off('video_status', handleVideoStatus); };
  }, [handleVideoStatus]);

  useEffect(() => {
    if (editingMaterial) {
      setFormData({
        title: editingMaterial.title,
        body: editingMaterial.body,
        status: editingMaterial.status || 'active',
        order: editingMaterial.order ?? 1,
        video_links: editingMaterial.video_links.length > 0 ? editingMaterial.video_links : [''],
      });
      setReusedFiles([]);
      setReusedVideos([]);
    } else if (reuseSource) {
      // Prefill from the library source; its files/videos are carried over
      // as references (existing_files / existing_videos) rather than
      // re-uploaded, matching the backend reuse contract.
      setFormData({
        title: reuseSource.title ?? '',
        body: reuseSource.body ?? '',
        status: 'active',
        order: 1,
        video_links: reuseSource.video_links && reuseSource.video_links.length > 0 ? reuseSource.video_links : [''],
      });
      setReusedFiles(
        (reuseSource.files ?? []).map((f: any) => ({
          key: f.key,
          file_name: f.file_name,
          size: f.size,
        }))
      );
      setReusedVideos(
        (reuseSource.videos ?? []).map((v: any) => ({
          video_id: v.video_id,
          original_name: v.original_name,
        }))
      );
    } else {
      setFormData({ title: '', body: '', status: 'active', order: 1, video_links: [''] });
      setReusedFiles([]);
      setReusedVideos([]);
    }
    setUploadedFiles([]);
    setUploadedVideos([]);
    setErrors({});
    setError(null);
    setUploadProgress(0);
    setVideoProcessingStatus({});
    pendingVideoIds.current.clear();
  }, [editingMaterial, reuseSource, visible]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleVideoLinkChange = (index: number, value: string) => {
    const newLinks = [...formData.video_links];
    newLinks[index] = value;
    setFormData((prev) => ({ ...prev, video_links: newLinks }));
  };
  const addVideoLink = () => {
    if (formData.video_links.length < 5) setFormData((prev) => ({ ...prev, video_links: [...prev.video_links, ''] }));
  };
  const removeVideoLink = (index: number) => {
    const newLinks = formData.video_links.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, video_links: newLinks.length > 0 ? newLinks : [''] }));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };
  const getFileExtension = (filename: string): string => filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase();
  const getTotalFilesSize = (): number => uploadedFiles.reduce((total, f) => total + f.size, 0);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (uploadedFiles.length + reusedFiles.length + files.length > MAX_FILES) {
      showError(`Maximum ${MAX_FILES} files allowed`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const newFiles: UploadedFile[] = [];
    const fileErrors: string[] = [];
    Array.from(files).forEach((file) => {
      const ext = getFileExtension(file.name);
      if (!ALLOWED_FORMATS.includes(file.type) && !ALLOWED_EXTENSIONS.includes(ext)) {
        fileErrors.push(`${file.name}: Invalid format`);
        return;
      }
      newFiles.push({ file, name: file.name, size: file.size });
    });
    const wouldBeTotal = getTotalFilesSize() + newFiles.reduce((s, f) => s + f.size, 0);
    if (wouldBeTotal > MAX_TOTAL_SIZE) {
      showError(`Total size cannot exceed ${formatFileSize(MAX_TOTAL_SIZE)}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (fileErrors.length > 0) showError(fileErrors.join(', '));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleRemoveFile = (index: number) => setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  const handleRemoveReusedFile = (key: string) => setReusedFiles((prev) => prev.filter((f) => f.key !== key));

  const handleVideoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    if (uploadedVideos.length + reusedVideos.length + files.length > MAX_VIDEOS) {
      showError(`Maximum ${MAX_VIDEOS} videos allowed`);
      if (videoInputRef.current) videoInputRef.current.value = '';
      return;
    }
    const newVideos: UploadedVideo[] = [];
    const videoErrors: string[] = [];
    Array.from(files).forEach((file) => {
      const ext = getFileExtension(file.name);
      if (!ALLOWED_VIDEO_TYPES.includes(file.type) && !ALLOWED_VIDEO_EXTS.includes(ext)) {
        videoErrors.push(`${file.name}: Invalid format. Allowed: MP4, MOV, AVI, MKV`);
        return;
      }
      if (file.size > MAX_VIDEO_SIZE) {
        videoErrors.push(`${file.name}: Exceeds 500MB limit`);
        return;
      }
      newVideos.push({ file, name: file.name, size: file.size });
    });
    if (videoErrors.length > 0) {
      showError(videoErrors.join('\n'));
      if (videoInputRef.current) videoInputRef.current.value = '';
      return;
    }
    setUploadedVideos((prev) => [...prev, ...newVideos]);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };
  const handleRemoveVideo = (index: number) => setUploadedVideos((prev) => prev.filter((_, i) => i !== index));
  const handleRemoveReusedVideo = (videoId: string) => setReusedVideos((prev) => prev.filter((v) => v.video_id !== videoId));

  const getFileIcon = (filename: string): string => {
    const ext = getFileExtension(filename);
    switch (ext) {
      case 'pdf': return 'pi pi-file-pdf';
      case 'doc': case 'docx': return 'pi pi-file-word';
      case 'xls': case 'xlsx': return 'pi pi-file-excel';
      case 'jpg': case 'jpeg': case 'png': return 'pi pi-image';
      default: return 'pi pi-file';
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.body.trim()) newErrors.body = 'Content is required';
    if (!formData.status) newErrors.status = 'Status is required';
    if (!formData.order || formData.order < 1) newErrors.order = 'Order must be a positive number';

    const validLinks = formData.video_links.filter((l) => l.trim() !== '');
    if (validLinks.length > 5) newErrors.video_links = 'Maximum 5 video links allowed';
    for (const link of validLinks) {
      try { new URL(link); } catch {
        newErrors.video_links = 'All video links must be valid URLs';
        break;
      }
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      showError('Please correct all highlighted fields', 'Validation Failed');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    setUploadProgress(0);

    const payload = new FormData();
    payload.append('course_id', courseId);
    if (chapterId) payload.append('chapter_id', chapterId); // omitted entirely → course-general
    payload.append('title', formData.title.trim());
    payload.append('body', formData.body.trim());
    payload.append('status', formData.status);
    payload.append('order', String(formData.order));

    formData.video_links.filter((l) => l.trim() !== '').forEach((link) => payload.append('video_links[]', link.trim()));
    uploadedFiles.forEach((f) => payload.append('files[]', f.file));
    uploadedVideos.forEach((v) => payload.append('videos[]', v.file));

    if (reusedFiles.length > 0) {
      payload.append('existing_files', JSON.stringify(reusedFiles.map((f) => ({ key: f.key }))));
    }
    if (reusedVideos.length > 0) {
      payload.append('existing_videos', JSON.stringify(reusedVideos.map((v) => ({ video_id: v.video_id }))));
    }

    try {
      const isEditing = !!editingMaterial;
      const response = isEditing
        ? await apiClient.put(`/v1/practical-materials/${editingMaterial!.id}`, payload, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / (e.total ?? 1))),
          })
        : await apiClient.post('/v1/practical-materials', payload, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / (e.total ?? 1))),
          });

      if (response.data.success) {
        const videosData: any[] = response.data.data?.videos ?? [];
        const videosProcessing  = response.data.data?.videos_processing ?? 0;

        if (videosProcessing > 0 && videosData.length > 0) {
          videosData.forEach((v: any) => {
            if (v.video_id) {
              pendingVideoIds.current.add(v.video_id);
              setVideoProcessingStatus((prev) => ({ ...prev, [v.video_id]: 'processing' }));
            }
          });
          await initSocketListener();
          showSuccess('Practical Material Saved', `Saved successfully. ${videosProcessing} video(s) are processing — you'll be notified here when ready.`);
        } else {
          showSuccess('Practical Material Saved', isEditing ? 'Practical material updated successfully.' : 'Practical material created successfully.');
        }

        onSuccess?.();
        setTimeout(() => onHide(), 900);
      } else {
        showError(response.data.message || 'Operation failed');
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || `HTTP ${err.response?.status}: Failed to save practical material.`;
      showError(msg);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    setFormData({ title: '', body: '', status: 'active', order: 1, video_links: [''] });
    setUploadedFiles([]);
    setUploadedVideos([]);
    setReusedFiles([]);
    setReusedVideos([]);
    setErrors({});
    setError(null);
    setUploadProgress(0);
    setVideoProcessingStatus({});
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
    onHide();
  };

  const totalSize       = getTotalFilesSize();
  const canUploadMore   = (uploadedFiles.length + reusedFiles.length) < MAX_FILES && totalSize < MAX_TOTAL_SIZE;
  const canUploadVideo  = (uploadedVideos.length + reusedVideos.length) < MAX_VIDEOS;
  const processingCount = Object.values(videoProcessingStatus).filter((s) => s === 'processing').length;

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '900px' }}
      dismissableMask
      draggable={false}
      pt={{ root: { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' } } }}
    >
      <Toast ref={toast} position="top-right" />

      <div>
        <div style={{ background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)', borderRadius: '16px', padding: '24px', marginBottom: '24px', color: 'white', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '150px', height: '150px', background: 'rgba(212, 241, 126, 0.1)', borderRadius: '50%', filter: 'blur(40px)' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-block', padding: '4px 10px', background: 'rgba(212, 241, 126, 0.2)', borderRadius: '16px', marginBottom: '8px', fontSize: '0.65rem', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              Practical Material Management
            </div>
            <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: '700' }}>
              {editingMaterial ? 'Edit Practical Material' : reuseSource ? 'Reuse Practical Material' : 'Create Practical Material'}
            </h2>
            <p style={{ margin: '0', color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
              {chapterId ? 'Attached to the selected chapter' : 'General material — applies to the whole course, no specific chapter'}
            </p>
            {reuseSource && !editingMaterial && (
              <div style={{ marginTop: '12px', padding: '8px 14px', background: 'rgba(212, 241, 126, 0.15)', border: '1px solid rgba(212, 241, 126, 0.3)', borderRadius: '10px', fontSize: '0.8rem' }}>
                <i className="pi pi-copy" style={{ marginRight: '8px' }} />
                Prefilled from an existing library material — its attached files/videos will be reused without re-uploading.
              </div>
            )}
            {processingCount > 0 && (
              <div style={{ marginTop: '12px', padding: '8px 14px', background: 'rgba(212, 241, 126, 0.15)', border: '1px solid rgba(212, 241, 126, 0.3)', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem' }}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.9rem' }} />
                {processingCount} video{processingCount > 1 ? 's' : ''} processing in background... You'll be notified when ready.
              </div>
            )}
          </div>
        </div>

        {error && <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem' }} />}

        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <GlobalInputField
                  label="Material Title"
                  required
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="e.g., Titration Lab Worksheet"
                  error={errors.title}
                />
              </div>
              <div style={{ width: '120px' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Order <span style={{ color: '#e53e3e' }}>*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  value={formData.order}
                  onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 1)}
                  style={{ width: '100%', padding: '10px 12px', border: errors.order ? '1px solid #e53e3e' : '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', color: '#1a1a1a', background: 'white' }}
                />
                {errors.order && <small style={{ color: '#e53e3e', marginTop: '4px', display: 'block', fontSize: '0.75rem' }}>{errors.order}</small>}
              </div>
            </div>

            <GlobalTextArea
              label="Material Content"
              required
              value={formData.body}
              onChange={(e) => handleInputChange('body', e.target.value)}
              placeholder="Instructions, procedure, expected outcomes..."
              error={errors.body}
              rows={8}
            />

            <GlobalDropdownField
              label="Status"
              required
              value={formData.status}
              onChange={(val) => handleInputChange('status', val)}
              options={statusOptions}
              placeholder="Select status"
              error={errors.status}
            />

            {/* External Video Links */}
            <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '12px', border: '1px solid #e9ecef' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e' }}>
                  <i className="pi pi-link" style={{ marginRight: '8px' }} /> External Video Links (Optional)
                </h3>
                <span style={{ fontSize: '0.8rem', color: '#666' }}>{formData.video_links.filter((l) => l.trim()).length}/5</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {formData.video_links.map((link, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <GlobalInputField value={link} onChange={(e) => handleVideoLinkChange(index, e.target.value)} placeholder="https://youtube.com/watch?v=..." error="" />
                    {formData.video_links.length > 1 && (
                      <button type="button" onClick={() => removeVideoLink(index)} style={{ padding: '10px 12px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px', cursor: 'pointer', flexShrink: 0 }}>
                        <i className="pi pi-trash" style={{ fontSize: '0.85rem' }} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {errors.video_links && <small style={{ color: '#e53e3e', marginTop: '8px', display: 'block' }}>{errors.video_links}</small>}
              {formData.video_links.length < 5 && (
                <button type="button" onClick={addVideoLink} style={{ marginTop: '12px', padding: '8px 14px', background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', color: '#0f3d3e', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', width: '100%' }}>
                  <i className="pi pi-plus" style={{ marginRight: '6px' }} /> Add Another Video Link
                </button>
              )}
            </div>

            {/* Video File Uploads */}
            <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '12px', border: '1px solid #e9ecef' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e' }}>
                  <i className="pi pi-video" style={{ marginRight: '8px' }} /> Videos (Optional)
                </h3>
                <span style={{ fontSize: '0.8rem', color: '#666' }}>{uploadedVideos.length + reusedVideos.length}/{MAX_VIDEOS} • Max 500MB each</span>
              </div>
              <input ref={videoInputRef} type="file" multiple accept=".mp4,.mov,.avi,.mkv" onChange={handleVideoSelect} style={{ display: 'none' }} />
              <button type="button" onClick={() => videoInputRef.current?.click()} disabled={!canUploadVideo} style={{ width: '100%', padding: '14px', border: '2px dashed #cbd5e0', borderRadius: '8px', background: canUploadVideo ? 'white' : '#f5f5f5', color: canUploadVideo ? '#0f3d3e' : '#999', cursor: canUploadVideo ? 'pointer' : 'not-allowed', fontSize: '0.9rem', fontWeight: '500' }}>
                <i className="pi pi-upload" style={{ marginRight: '8px' }} /> {canUploadVideo ? 'Choose video files' : 'Limit reached (3 videos max)'}
              </button>
              <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#666', textAlign: 'center' }}>Allowed: MP4, MOV, AVI, MKV • Processed in background after upload</div>

              {reusedVideos.length > 0 && (
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {reusedVideos.map((v) => (
                    <div key={v.video_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        <i className="pi pi-copy" style={{ fontSize: '1.2rem', color: '#4338ca' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.original_name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#4338ca' }}>Reused from library</div>
                        </div>
                      </div>
                      <button onClick={() => handleRemoveReusedVideo(v.video_id)} style={{ padding: '6px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Remove</button>
                    </div>
                  ))}
                </div>
              )}

              {uploadedVideos.length > 0 && (
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {uploadedVideos.map((videoObj, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        <i className="pi pi-play-circle" style={{ fontSize: '1.4rem', color: '#0f3d3e' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{videoObj.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>{formatFileSize(videoObj.size)}</div>
                        </div>
                      </div>
                      <button onClick={() => handleRemoveVideo(index)} style={{ padding: '6px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Remove</button>
                    </div>
                  ))}
                </div>
              )}

              {Object.entries(videoProcessingStatus).length > 0 && (
                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {Object.entries(videoProcessingStatus).map(([videoId, status]) => (
                    <div key={videoId} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '8px', background: status === 'ready' ? '#f0fdf4' : status === 'failed' ? '#fef2f2' : '#fffbeb', border: `1px solid ${status === 'ready' ? '#bbf7d0' : status === 'failed' ? '#fecaca' : '#fde68a'}`, fontSize: '0.8rem' }}>
                      {status === 'processing' && <i className="pi pi-spin pi-spinner" style={{ color: '#d97706' }} />}
                      {status === 'ready' && <i className="pi pi-check-circle" style={{ color: '#16a34a' }} />}
                      {status === 'failed' && <i className="pi pi-times-circle" style={{ color: '#dc2626' }} />}
                      <span style={{ color: status === 'ready' ? '#15803d' : status === 'failed' ? '#dc2626' : '#92400e' }}>
                        {videoId} — {status === 'processing' && 'Processing...'}{status === 'ready' && 'Ready ✓'}{status === 'failed' && 'Processing failed'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* File Attachments */}
            <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '12px', border: '1px solid #e9ecef' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e' }}>
                  <i className="pi pi-paperclip" style={{ marginRight: '8px' }} /> File Attachments (Optional)
                </h3>
                <span style={{ fontSize: '0.8rem', color: '#666' }}>{uploadedFiles.length + reusedFiles.length}/{MAX_FILES} • {formatFileSize(totalSize)} / {formatFileSize(MAX_TOTAL_SIZE)}</span>
              </div>
              <input ref={fileInputRef} type="file" multiple accept=".pdf,.xls,.xlsx,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileSelect} style={{ display: 'none' }} />
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={!canUploadMore} style={{ width: '100%', padding: '14px', border: '2px dashed #cbd5e0', borderRadius: '8px', background: canUploadMore ? 'white' : '#f5f5f5', color: canUploadMore ? '#0f3d3e' : '#999', cursor: canUploadMore ? 'pointer' : 'not-allowed', fontSize: '0.9rem', fontWeight: '500' }}>
                <i className="pi pi-upload" style={{ marginRight: '8px' }} /> {canUploadMore ? 'Choose files (max 10MB total)' : 'Limit reached'}
              </button>
              <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#666', textAlign: 'center' }}>Allowed: PDF, Word, Excel, JPEG, PNG</div>

              {reusedFiles.length > 0 && (
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {reusedFiles.map((f) => (
                    <div key={f.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        <i className="pi pi-copy" style={{ fontSize: '1.4rem', color: '#4338ca' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.file_name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#4338ca' }}>{formatFileSize(f.size)} • Reused from library</div>
                        </div>
                      </div>
                      <button onClick={() => handleRemoveReusedFile(f.key)} style={{ padding: '6px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Remove</button>
                    </div>
                  ))}
                </div>
              )}

              {uploadedFiles.length > 0 && (
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {uploadedFiles.map((fileObj, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        <i className={getFileIcon(fileObj.name)} style={{ fontSize: '1.4rem', color: '#0f3d3e' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fileObj.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>{formatFileSize(fileObj.size)}</div>
                        </div>
                      </div>
                      <button onClick={() => handleRemoveFile(index)} style={{ padding: '6px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>

        {loading && uploadProgress > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '0.8rem', color: '#666' }}>{uploadProgress < 100 ? 'Uploading to server...' : 'Processing...'}</span>
              <span style={{ fontSize: '0.8rem', color: '#0f3d3e', fontWeight: '600' }}>{uploadProgress}%</span>
            </div>
            <div style={{ background: '#e9ecef', borderRadius: '8px', height: '8px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${uploadProgress}%`, background: 'linear-gradient(90deg, #0f3d3e, #1a5555)', borderRadius: '8px', transition: 'width 0.3s ease' }} />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <GlobalButton label="Cancel" variant="outline" onClick={handleClose} disabled={loading} />
          <GlobalButton
            label={loading ? (uploadProgress < 100 ? `Uploading ${uploadProgress}%...` : editingMaterial ? 'Updating...' : 'Creating...') : editingMaterial ? 'Update Material' : 'Create Material'}
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
            loading={loading}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default AddPracticalMaterialModal;